/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {BaseDB_ApiGeneratorCaller} from "@intuitionrobotics/db-api-generator/frontend";
import {AlertFeedback, AlertStatus, Api_UpdateAllAlerts, DB_Alert, DB_Comment, FeedbackCategory} from "@app/app-shared/alert";
import {ThunderDispatcher, XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {feedbackCategories} from "../app/_to_remove/AlertFeedbackCategories";
import {upsertImpl} from "./utils";
import {BaseHttpRequest, ErrorResponse, HttpMethod} from "@intuitionrobotics/thunderstorm";
import {DemoDataUtils} from "../demo/Patients_DemoData";
import {CommentsModule} from "@modules/CommentsModule";
import {auditBy, DB_Object, PartialProperties, sortArray} from "@intuitionrobotics/ts-common";
import {UsersModule} from "@modules/UsersModule";
import {PatientsMap} from "../app/pages/ui/patients/PatientsContext";

export interface OnAlertsUpdated {
    __onAlertsUpdated: (alertId?: string) => void;
}

export const dispatch_onAlertsUpdated = new ThunderDispatcher<OnAlertsUpdated, "__onAlertsUpdated">("__onAlertsUpdated");

export class AlertsModule_Class
    extends BaseDB_ApiGeneratorCaller<DB_Alert<any>> {

    private alerts: DB_Alert<any>[] | undefined;
    private feedbackCategories: FeedbackCategory[] = feedbackCategories;

    constructor() {
        super({key: "alert", relativeUrl: "/v1/alert"}, "AlertsModule");
    }

    protected onError(request: BaseHttpRequest<any>, resError?: ErrorResponse<any>): boolean {
        this.logError(request.errorMessage, resError);
        return true;
    }

    getFeedbackCategories = () => this.feedbackCategories;

    getAlerts = (patientsMap: PatientsMap) => this.alerts?.filter(a => !a.patientId || patientsMap[a.patientId]);
    getAllAlerts = () => this.alerts;
    resetAlerts = () => {
        this.alerts = undefined;
        this.query();
    }

    getPatientAlerts = (patientId: string) => {
        const patientAlerts = this.alerts?.filter(alert => alert.patientId === patientId);
        if (!patientAlerts)
            return;

        return sortArray(patientAlerts, a => a._updated.auditAt.timestamp);
    };

    // We don't return REJECTED.
    // New alerts mean status == RECEIVED.
    getNewAlerts = (patientId?: string) => {
        if (!patientId)
            return [];

        return this.getPatientAlerts(patientId)?.filter((alert: DB_Alert<any>) => this.isNew(alert)) || [];
    };

    existsUnresolvedAlert = (patientsMap: PatientsMap) => {
        const r = this.getAlerts(patientsMap)
            ?.filter(this.needsFeedback);
        return r !== undefined && r.length > 0;
    };

    getUnresolvedPatientAlerts = (patientId: string) => {
        const patientAlerts = this.getPatientAlerts(patientId);
        if (!patientAlerts)
            return;

        return sortArray(patientAlerts.filter(this.needsFeedback), a => a._updated.auditAt.timestamp);
    };

    existsUnresolvedPatientAlert = (patientId: string) => {
        const r = this.getUnresolvedPatientAlerts(patientId);
        return r !== undefined && r.length > 0;
    };

    demoMarkResolved = (alert: DB_Alert<any>) => {
        this.upsertImpl(alert);
    };

    async onEntryCreated(response: DB_Alert<any>): Promise<void> {
        this.upsertImpl(response);
    }

    async onEntryDeleted(response: DB_Alert<any>): Promise<void> {
    }

    async onEntryUpdated(response: DB_Alert<any>): Promise<void> {
        CommentsModule.query();
        this.upsertImpl(response);
    }

    async onQueryReturned(response: DB_Alert<any>[]): Promise<void> {
        this.alerts = this.alerts || [];
        response.reduce((carry: DB_Alert<any>[], el) => {
            if (el.status === AlertStatus.REJECTED) {
                const idx = carry.findIndex(a => a._id === el._id);
                if (idx !== -1)
                    carry.splice(idx, 1);

                return carry;
            }

            return upsertImpl(carry, el);
        }, this.alerts);
        dispatch_onAlertsUpdated.dispatchUI();
    }

    sortAlertsByFeedback = (alerts: DB_Alert<any>[]) => {
        alerts.sort((a, b) => {
            const aNeedsFeedback = this.needsFeedback(a);
            const bNeedsFeedback = this.needsFeedback(b);
            if (aNeedsFeedback && !bNeedsFeedback)
                return -1;

            if (!aNeedsFeedback && bNeedsFeedback)
                return 1;

            return a._created.auditAt.timestamp < b._created.auditAt.timestamp ? 1 : -1;
        });
    };

    async onGotUnique(response: DB_Alert<any>): Promise<void> {
        this.upsertImpl(response);
    }

    public upsertImpl = (item: DB_Alert<any>) => {
        if (item.status === AlertStatus.REJECTED) {
            dispatch_onAlertsUpdated.dispatchUI(item._id);
            return;
        }

        this.alerts = this.alerts || [];
        const toRet = upsertImpl(this.alerts, item);
        dispatch_onAlertsUpdated.dispatchUI(item._id);
        return toRet;
    };

    needsFeedback = (alert: DB_Alert<any>) => alert.status !== AlertStatus.COMPLETE;

    // Only useful when we need to display it as new.
    isNew = (alert: DB_Alert<any>) => alert.status === AlertStatus.NEW;

    markAlertAsNotNew(patientId: string) {
        const notViewedPatientAlerts = this.getNewAlerts(patientId);
        if (!notViewedPatientAlerts || !notViewedPatientAlerts.length)
            return;

        this.updateAll(patientId);
    };

    private updateAll = (patientId: string) => {
        XhrHttpModule
            .createRequest<Api_UpdateAllAlerts>(HttpMethod.POST, "update-all-alerts")
            .setJsonBody({patientId})
            .setRelativeUrl("/v1/alert/update-all")
            // TODO: Probably need a retry
            .setOnError((request: BaseHttpRequest<Api_UpdateAllAlerts>, resError?: ErrorResponse<any>) => {
                this.logWarning("failed to update all alerts..", resError);
            })
            .execute(response => {
                return this.onQueryReturned(response);
            });

    };

    markAsResolved(alert: DB_Alert<any>, feedback: AlertFeedback) {
        const currentUser = UsersModule.getCurrentUserByEmail();
        const newComment: PartialProperties<DB_Comment<"feedback">, keyof DB_Object> = {
            type: "feedback",
            data: feedback,
            timestamp: auditBy(currentUser?.mail || "fe-stub"),
            alertId: alert._id
        };
        CommentsModule.createFeedback(newComment, alert);

    }

    public setDemoAlerts = (patientIds: string[]) => {
        this.alerts = patientIds.reduce((allAlerts: DB_Alert<any>[], pId) => [...allAlerts, ...DemoDataUtils.generateNewAlertsForPatients(pId)], []);
        dispatch_onAlertsUpdated.dispatchUI();
    }
}


export const AlertsModule = new AlertsModule_Class();
